import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  {
    type: "p",
    text: "When a visitor shows buying, hiring, partnership or investment intent, the twin captures their name and email in-conversation and scores the lead. Leads appear in your dashboard and can stream to your CRM in real time.",
  },
  { type: "h2", text: "In the dashboard" },
  {
    type: "ul",
    items: [
      "Every lead lands on the Leads page with the full conversation, an intent/lead score, status and notes.",
      "Export to CSV any time.",
      "New hot leads also surface in the notification center and (optionally) by email.",
    ],
  },
  { type: "h2", text: "CRM webhook" },
  {
    type: "p",
    text: "In Settings → Integrations, set a webhook URL. Every captured lead is POSTed to it as JSON, so you can pipe leads into HubSpot, Pipedrive, Salesforce, Zapier, Make or your own endpoint.",
  },
  { type: "h3", text: "Signature verification" },
  {
    type: "p",
    text: "Each request carries an X-RTN-Signature header: an HMAC-SHA256 of the raw request body using your signing secret (shown next to the webhook URL). Verify it before trusting the payload.",
  },
  {
    type: "code",
    lang: "javascript",
    code: `import crypto from "node:crypto";

function verify(rawBody, signature, secret) {
  const expected = crypto
    .createHmac("sha256", secret)
    .update(rawBody)
    .digest("hex");
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature),
  );
}`,
  },
  { type: "h3", text: "Payload shape" },
  {
    type: "code",
    lang: "json",
    code: `{
  "event": "lead.created",
  "lead": {
    "name": "Jane Doe",
    "email": "jane@acme.com",
    "message": "Interested in the Pro plan for 20 seats",
    "score": 82,
    "status": "warm",
    "company_id": "…",
    "created_at": "2026-07-28T10:00:00Z"
  }
}`,
  },
  {
    type: "callout",
    title: "Test it first",
    text: 'Use "Send test event" in Settings → Integrations to fire a sample payload at your endpoint and confirm your signature check and mapping work before going live.',
  },
];

export const Route = createFileRoute("/docs/leads-and-crm")({
  head: () =>
    buildSeo({
      path: "/docs/leads-and-crm",
      title: "Leads & CRM Webhooks — Reality Twin Docs",
      description:
        "Capture leads and stream them to your CRM with an HMAC-signed webhook. Payload and verification.",
    }),
  component: () => (
    <DocsPageShell
      slug="leads-and-crm"
      title="Leads & CRM webhooks"
      description="Capture leads and stream them to your CRM in real time, HMAC-signed."
      blocks={blocks}
    />
  ),
});
